import React, { useState } from 'react';
import { useGetUsersReportQuery, useGetCountriesQuery, useGetCitiesQuery } from '../../../store/api/adminApi';
import PieChart from '../../../components/charts/PieChart';
import BarChart from '../../../components/charts/BarChart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendUp,
  faArrowTrendDown,
  faUsers,
  faUserPlus,
  faClock,
  faCalendarWeek,
  faCalendarDay,
  faStar,
  faMobileAlt,
  faFunnelDollar,
  faGlobe,
  faRotateRight,
} from '@fortawesome/free-solid-svg-icons';

const PERIOD_OPTIONS = [
  { value: 'last_7_days',  label: 'Last 7 Days'  },
  { value: 'last_30_days', label: 'Last 30 Days'  },
  { value: 'last_90_days', label: 'Last 90 Days'  },
  { value: 'this_year',    label: 'This Year'     },
];

const CATEGORY_COLORS = [
  'rgba(57,106,84,0.85)',
  'rgba(57,106,84,0.60)',
  'rgba(33,37,41,0.70)',
  'rgba(248,168,36,0.85)',
  'rgba(239,68,68,0.75)',
  'rgba(59,130,246,0.75)',
  'rgba(168,85,247,0.75)',
  'rgba(100,116,139,0.70)',
];

const DEVICE_COLORS = [
  'rgba(57,106,84,0.85)',
  'rgba(59,130,246,0.80)',
  'rgba(100,116,139,0.65)',
];

const FUNNEL_COLORS = [
  'rgba(57,106,84,0.85)',
  'rgba(57,106,84,0.70)',
  'rgba(57,106,84,0.55)',
  'rgba(57,106,84,0.42)',
  'rgba(57,106,84,0.30)',
  'rgba(57,106,84,0.18)',
];

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${parseFloat((n / 1_000_000).toFixed(1))}M`;
  if (n >= 1_000)     return `${parseFloat((n / 1_000).toFixed(1))}k`;
  return n.toString();
}

/* ─── Skeleton helpers ─────────────────────────────────────────────── */
const SkeletonBox: React.FC<{ width?: string; height?: number; className?: string }> = ({
  width = '100%', height = 16, className = '',
}) => (
  <div
    className={className}
    style={{
      width,
      height,
      borderRadius: 6,
      background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.4s infinite',
    }}
  />
);

const StatCardSkeleton: React.FC = () => (
  <div className="col-6 col-xl-4 col-xxl-2">
    <div className="card h-100">
      <div className="card-body p-6">
        <div className="d-flex align-items-start justify-content-between mb-4">
          <SkeletonBox width="50px" height={22} />
          <SkeletonBox width="40px" height={40} className="rounded" />
        </div>
        <SkeletonBox width="70px" height={28} className="mb-2" />
        <SkeletonBox width="120px" height={14} />
      </div>
    </div>
  </div>
);

const ChartCardSkeleton: React.FC<{ height?: number }> = ({ height = 280 }) => (
  <div className="card mb-6">
    <div className="card-body p-6">
      <SkeletonBox width="180px" height={20} className="mb-2" />
      <SkeletonBox width="240px" height={14} className="mb-6" />
      <SkeletonBox width="100%" height={height} />
    </div>
  </div>
);

/* ─── Stat Card ────────────────────────────────────────────────────── */
interface StatCardProps {
  label: string;
  value: string;
  change: number | null;
  icon: any;
  iconColor?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, change, icon, iconColor = '#396A54' }) => (
  <div className="col-6 col-xl-4 col-xxl-2">
    <div className="card h-100">
      <div className="card-body p-6">
        <div className="d-flex align-items-start justify-content-between mb-4">
          <div className="d-flex flex-column gap-1">
            {change !== null ? (
              <span
                className="badge fw-semibold fs-8 d-inline-flex align-items-center gap-1"
                style={{
                  background: change >= 0 ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                  color: change >= 0 ? '#16a34a' : '#dc2626',
                  width: 'fit-content',
                }}
              >
                <FontAwesomeIcon icon={change >= 0 ? faArrowTrendUp : faArrowTrendDown} style={{ fontSize: 10 }} />
                {change >= 0 ? '+' : ''}{change}%
              </span>
            ) : (
              <span style={{ height: 22, display: 'block' }} />
            )}
          </div>
          <div
            className="d-flex align-items-center justify-content-center rounded"
            style={{ width: 40, height: 40, background: `${iconColor}18`, flexShrink: 0 }}
          >
            <FontAwesomeIcon icon={icon} style={{ color: iconColor, fontSize: 18 }} />
          </div>
        </div>
        <div className="fw-bold text-dark mb-1" style={{ fontSize: 28, lineHeight: 1 }}>{value}</div>
        <div className="text-muted fw-semibold fs-7">{label}</div>
      </div>
    </div>
  </div>
);

/* ─── Main Component ───────────────────────────────────────────────── */
const AdminUsersReport: React.FC = () => {
  const [countryId, setCountryId] = useState('');
  const [cityId,    setCityId]    = useState('');
  const [period,    setPeriod]    = useState('last_30_days');

  const params: Record<string, any> = { period };
  if (countryId) params.country_id = countryId;
  if (cityId)    params.city_id    = cityId;

  const {
    data,
    isLoading,   // true only on the very first fetch (no cached data yet)
    isFetching,  // true on every in-flight request including re-fetches
    isError,
    refetch,
  } = useGetUsersReportQuery(params);

  const { data: countriesData } = useGetCountriesQuery({ per_page: 200 });
  const { data: citiesData }    = useGetCitiesQuery(
    countryId ? { country_id: countryId, per_page: 500 } : { per_page: 500 }
  );

  const d = data?.data || {};

  const countries: any[] = countriesData?.data?.data || countriesData?.data || [];
  const cities: any[]    = citiesData?.data?.data    || citiesData?.data    || [];

  const deviceDist:     Array<{ platform: string; count: number; percentage: number }>         = d.device_distribution || [];
  const userByCat:      Array<{ label: string; count: number; percentage: number }>            = d.user_by_category    || [];
  const convFunnel:     Array<{ step: number; label: string; count: number }>                  = d.conversion_funnel   || [];
  const sessionCountry: Array<{ country: string; avg_minutes: number }>                        = d.session_per_country || [];

  const statCards: StatCardProps[] = [
    { label: 'Survival rate (30 days)',    value: `${d.survival_rate ?? 0}%`,          change: d.survival_change     ?? null, icon: faUsers,       iconColor: '#396A54' },
    { label: 'New users',                  value: formatNumber(d.new_users ?? 0),       change: d.new_users_change    ?? null, icon: faUserPlus,    iconColor: '#8b5cf6' },
    { label: 'Average session duration',   value: d.avg_session_formatted ?? '0s',      change: d.session_change      ?? null, icon: faClock,       iconColor: '#f59e0b' },
    { label: 'Weekly active users',        value: formatNumber(d.wau ?? 0),             change: d.wau_change          ?? null, icon: faCalendarWeek,iconColor: '#ef4444' },
    { label: 'Daily active users',         value: formatNumber(d.dau ?? 0),             change: d.dau_change          ?? null, icon: faCalendarDay, iconColor: '#22c55e' },
    { label: 'Average user rating',        value: `${d.avg_rating ?? 0} / 5`,           change: null,                          icon: faStar,        iconColor: '#f6c000' },
  ];

  return (
    <>
      {/* shimmer animation */}
      <style>{`
        @keyframes shimmer {
          0%   { background-position: -200% 0; }
          100% { background-position:  200% 0; }
        }
      `}</style>

    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">

        {/* Toolbar */}
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                Users Report
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted"><span className="text-muted">Home</span></li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">Reports &amp; Stats</li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">Users Report</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">

            {/* Thin refetch progress bar — shown when re-fetching over stale data */}
            {isFetching && !isLoading && (
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, height: 3, zIndex: 9999,
                background: 'linear-gradient(90deg, #396A54, #5ea87e, #396A54)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.2s infinite',
              }} />
            )}

            {/* Filter Bar — always visible immediately */}
            <div className="card mb-6">
              <div className="card-body py-5">
                <div className="row g-4 align-items-end">
                  <div className="col-12 col-md-4">
                    <label className="form-label fw-semibold text-muted fs-7 text-uppercase mb-2">Country</label>
                    <select
                      className="form-select form-select-solid"
                      value={countryId}
                      onChange={(e) => { setCountryId(e.target.value); setCityId(''); }}
                      disabled={isLoading}
                    >
                      <option value="">All countries</option>
                      {countries.map((c: any) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="form-label fw-semibold text-muted fs-7 text-uppercase mb-2">City</label>
                    <select
                      className="form-select form-select-solid"
                      value={cityId}
                      onChange={(e) => setCityId(e.target.value)}
                      disabled={isLoading}
                    >
                      <option value="">All cities</option>
                      {cities.map((c: any) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold text-muted fs-7 text-uppercase mb-2">Period</label>
                    <select
                      className="form-select form-select-solid"
                      value={period}
                      onChange={(e) => setPeriod(e.target.value)}
                      disabled={isLoading}
                    >
                      {PERIOD_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-1 d-flex align-items-end">
                    <button
                      className="btn btn-light-primary btn-sm w-100"
                      onClick={() => refetch()}
                      disabled={isFetching}
                      title="Refresh"
                    >
                      <FontAwesomeIcon icon={faRotateRight} className={isFetching ? 'fa-spin' : ''} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Error state ─────────────────────────────────────── */}
            {isError && (
              <div className="card mb-6">
                <div className="card-body d-flex flex-column align-items-center justify-content-center py-16 gap-4">
                  <i className="fas fa-triangle-exclamation text-warning" style={{ fontSize: 40 }}></i>
                  <div className="text-dark fw-bold fs-5">Failed to load report</div>
                  <div className="text-muted fs-7">The server took too long to respond. Please try again.</div>
                  <button className="btn btn-sm btn-primary" style={{ background: '#396A54', borderColor: '#396A54' }} onClick={() => refetch()}>
                    <FontAwesomeIcon icon={faRotateRight} className="me-2" />
                    Retry
                  </button>
                </div>
              </div>
            )}

            {/* ── Skeleton on first load ──────────────────────────── */}
            {isLoading && (
              <>
                <div className="row g-5 mb-6">
                  {[...Array(6)].map((_, i) => <StatCardSkeleton key={i} />)}
                </div>
                <div className="row g-6 mb-6">
                  <div className="col-12 col-xl-6"><ChartCardSkeleton height={240} /></div>
                  <div className="col-12 col-xl-6"><ChartCardSkeleton height={240} /></div>
                </div>
                <div className="row g-6">
                  <div className="col-12 col-xl-6"><ChartCardSkeleton height={280} /></div>
                  <div className="col-12 col-xl-6"><ChartCardSkeleton height={280} /></div>
                </div>
              </>
            )}

            {/* ── Data (shown immediately on re-fetches, faded while loading) */}
            {!isLoading && !isError && (
              <div style={{ opacity: isFetching ? 0.55 : 1, transition: 'opacity 0.2s' }}>

                {/* Stat Cards */}
                <div className="row g-5 mb-6">
                  {statCards.map((s) => (
                    <StatCard key={s.label} {...s} />
                  ))}
                </div>

                {/* Device + Category Charts */}
                <div className="card mb-6">
                  <div className="card-header pt-5 border-bottom-0">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark fs-4 d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faUsers} style={{ color: '#396A54' }} />
                        User distribution by category
                      </span>
                      <span className="text-muted mt-1 fw-semibold fs-7">
                        User classification by account type
                      </span>
                    </h3>
                  </div>
                  <div className="card-body pt-4">
                    <div className="row g-6">

                      {/* Device Distribution */}
                      <div className="col-12 col-xl-6">
                        <div className="border rounded p-6 h-100">
                          <div className="mb-4">
                            <div className="fw-bold text-dark fs-6">Device distribution</div>
                            <div className="text-muted fs-7">iOS vs. Android usage ratio</div>
                          </div>
                          {deviceDist.length > 0 ? (
                            <div className="row align-items-center g-4">
                              <div className="col-7">
                                <div style={{ height: 220 }}>
                                  <PieChart
                                    labels={deviceDist.map(d => d.platform)}
                                    data={deviceDist.map(d => d.count)}
                                    backgroundColor={DEVICE_COLORS}
                                    options={{
                                      responsive: true,
                                      maintainAspectRatio: false,
                                      cutout: '65%',
                                      plugins: { legend: { display: false } },
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="col-5">
                                <div className="d-flex flex-column gap-4">
                                  {deviceDist.map((d, i) => (
                                    <div key={d.platform} className="d-flex align-items-center gap-3">
                                      <span style={{
                                        width: 12, height: 12, borderRadius: '50%',
                                        background: DEVICE_COLORS[i % DEVICE_COLORS.length],
                                        flexShrink: 0,
                                      }} />
                                      <div>
                                        <div className="fw-semibold text-dark fs-7">{d.platform}</div>
                                        <div className="text-muted fs-8">{d.percentage}% · {d.count.toLocaleString()}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="d-flex align-items-center justify-content-center" style={{ height: 180 }}>
                              <div className="text-center">
                                <FontAwesomeIcon icon={faMobileAlt} className="text-muted mb-3" style={{ fontSize: 36, opacity: 0.25 }} />
                                <div className="text-muted fs-7">No device data available</div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* User Distribution by Category */}
                      <div className="col-12 col-xl-6">
                        <div className="border rounded p-6 h-100">
                          <div className="mb-4">
                            <div className="fw-bold text-dark fs-6">User distribution by category</div>
                            <div className="text-muted fs-7">User classification by account type</div>
                          </div>
                          {userByCat.length > 0 ? (
                            <div className="row align-items-center g-4">
                              <div className="col-7">
                                <div style={{ height: 220 }}>
                                  <PieChart
                                    labels={userByCat.map(c => c.label)}
                                    data={userByCat.map(c => c.count)}
                                    backgroundColor={CATEGORY_COLORS}
                                    options={{
                                      responsive: true,
                                      maintainAspectRatio: false,
                                      cutout: '65%',
                                      plugins: { legend: { display: false } },
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="col-5">
                                <div className="d-flex flex-column gap-3">
                                  {userByCat.map((c, i) => (
                                    <div key={c.label} className="d-flex align-items-center gap-3">
                                      <span style={{
                                        width: 12, height: 12, borderRadius: '50%',
                                        background: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
                                        flexShrink: 0,
                                      }} />
                                      <div>
                                        <div className="fw-semibold text-dark fs-7">{c.label}</div>
                                        <div className="text-muted fs-8">{c.percentage}%</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="d-flex align-items-center justify-content-center" style={{ height: 180 }}>
                              <span className="text-muted fs-7">No category data available</span>
                            </div>
                          )}
                        </div>
                      </div>

                    </div>
                  </div>
                </div>

                {/* Funnel + Session Per Country */}
                <div className="card mb-6">
                  <div className="card-body pt-6">
                    <div className="row g-6">

                      {/* Conversion Funnel */}
                      <div className="col-12 col-xl-6">
                        <div className="border rounded p-6 h-100">
                          <div className="mb-4">
                            <div className="fw-bold text-dark fs-6 d-flex align-items-center gap-2">
                              <FontAwesomeIcon icon={faFunnelDollar} style={{ color: '#396A54' }} />
                              Conversion Funnel
                            </div>
                            <div className="text-muted fs-7">User journey from open to deal</div>
                          </div>
                          {convFunnel.length > 0 ? (
                            <div style={{ height: 280 }}>
                              <BarChart
                                labels={convFunnel.map(f => f.label)}
                                datasets={[{
                                  label: 'Users',
                                  data: convFunnel.map(f => f.count),
                                  backgroundColor: FUNNEL_COLORS,
                                  borderColor: FUNNEL_COLORS,
                                  barThickness: 22,
                                }]}
                                options={{
                                  indexAxis: 'y',
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  plugins: { legend: { display: false } },
                                  scales: {
                                    x: { grid: { display: false }, ticks: { font: { size: 11 } } },
                                    y: { grid: { display: false }, ticks: { font: { size: 12 } } },
                                  },
                                }}
                              />
                            </div>
                          ) : (
                            <div className="d-flex align-items-center justify-content-center" style={{ height: 200 }}>
                              <span className="text-muted fs-7">No funnel data available</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Avg Session Per Country */}
                      <div className="col-12 col-xl-6">
                        <div className="border rounded p-6 h-100">
                          <div className="mb-4">
                            <div className="fw-bold text-dark fs-6 d-flex align-items-center gap-2">
                              <FontAwesomeIcon icon={faGlobe} style={{ color: '#396A54' }} />
                              Avg. Session Duration Per Country
                            </div>
                            <div className="text-muted fs-7">Average session in minutes (top 10 countries)</div>
                          </div>
                          {sessionCountry.length > 0 ? (
                            <div style={{ height: 280 }}>
                              <BarChart
                                labels={sessionCountry.map(s => s.country)}
                                datasets={[{
                                  label: 'Avg. Minutes',
                                  data: sessionCountry.map(s => s.avg_minutes),
                                  backgroundColor: 'rgba(57,106,84,0.75)',
                                  borderColor: 'rgba(57,106,84,1)',
                                  barThickness: 28,
                                }]}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  plugins: { legend: { display: false } },
                                  scales: {
                                    x: { grid: { display: false }, ticks: { font: { size: 11 } } },
                                    y: {
                                      grid: { color: 'rgba(0,0,0,0.05)' },
                                      ticks: { font: { size: 11 } },
                                      title: { display: true, text: 'Minutes', font: { size: 11 } },
                                    },
                                  },
                                }}
                              />
                            </div>
                          ) : (
                            <div className="d-flex align-items-center justify-content-center" style={{ height: 200 }}>
                              <span className="text-muted fs-7">No session data available</span>
                            </div>
                          )}
                        </div>
                      </div>

                    </div>
                  </div>
                </div>

              </div>
            )}

          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default AdminUsersReport;
